import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { ArrowLeft, Package, Sparkles, User } from 'lucide-react';
import { Loader2 } from 'lucide-react';

interface DropCollection {
  id: string;
  name: string | null;
  description: string | null;
  banner_url: string | null;
  icon_url: string | null;
  status: string;
  is_test: boolean;
  created_at: string;
  user_id: string;
  total_value_cents: number;
}

interface DropRarity {
  id: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary';
  count: number;
  media_url: string | null;
}

interface Profile {
  username: string;
  display_name: string;
}

const DropsCollectionDetail: React.FC = () => {
  const { collectionId } = useParams<{ collectionId: string }>();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [collection, setCollection] = useState<DropCollection | null>(null);
  const [rarities, setRarities] = useState<DropRarity[]>([]);
  const [creator, setCreator] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (collectionId) {
      loadCollection();
    }
  }, [collectionId]);

  const loadCollection = async () => {
    try {
      setLoading(true);

      // Load collection
      const { data: collectionData, error: collectionError } = await supabase
        .from('drop_collections')
        .select('*')
        .eq('id', collectionId)
        .single();

      if (collectionError) throw collectionError;

      setCollection(collectionData);

      // Load creator profile
      const { data: profileData } = await supabase
        .from('public_profiles')
        .select('username, display_name')
        .eq('id', collectionData.user_id)
        .single();

      if (profileData) {
        setCreator(profileData);
      }

      // Load rarities (using public view without payout info)
      const { data: raritiesData, error: raritiesError } = await supabase
        .from('drop_rarities_public')
        .select('*')
        .eq('collection_id', collectionId)
        .order('rarity');

      if (raritiesError) throw raritiesError;

      setRarities(raritiesData || []);
    } catch (error: any) {
      console.error('Error loading collection:', error);
      toast({
        title: 'Error',
        description: 'Failed to load collection',
        variant: 'destructive',
      });
      navigate('/drops');
    } finally {
      setLoading(false);
    }
  };

  const rarityColors = {
    common: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/40', text: 'text-emerald-300', dot: 'bg-emerald-500' },
    uncommon: { bg: 'bg-blue-500/20', border: 'border-blue-500/40', text: 'text-blue-300', dot: 'bg-blue-500' },
    rare: { bg: 'bg-purple-500/20', border: 'border-purple-500/40', text: 'text-purple-300', dot: 'bg-purple-500' },
    legendary: { bg: 'bg-orange-500/20', border: 'border-orange-500/40', text: 'text-orange-300', dot: 'bg-orange-500' },
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Collection not found</h2>
          <Button onClick={() => navigate('/drops')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Drops
          </Button>
        </div>
      </div>
    );
  }

  const totalDrops = rarities.reduce((sum, r) => sum + r.count, 0);
  const creatorName = creator?.display_name || creator?.username || 'Unknown';

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Back Button */}
      <Button
        onClick={() => navigate('/drops')}
        variant="ghost"
        className="mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Marketplace
      </Button>

      {/* Banner */}
      <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden mb-8 bg-gradient-to-br from-purple-500/20 to-pink-500/20">
        {collection.banner_url ? (
          <img
            src={collection.banner_url}
            alt={collection.name || 'Collection banner'}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Sparkles className="w-16 h-16 text-muted-foreground/30" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        
        {/* Collection Icon */}
        <div className="absolute bottom-6 left-6">
          <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-2xl border-4 border-background">
            {collection.icon_url ? (
              <img
                src={collection.icon_url}
                alt={collection.name || 'Icon'}
                className="w-full h-full object-cover rounded-xl"
              />
            ) : (
              <Sparkles className="w-12 h-12 md:w-16 md:h-16 text-white" />
            )}
          </div>
        </div>

        {/* Test Badge */}
        {collection.is_test && (
          <Badge className="absolute top-4 right-4 bg-blue-500/20 text-blue-400 border-blue-500/30">
            Test Drop
          </Badge>
        )}
      </div>

      {/* Collection Info */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          {collection.name || 'Untitled Collection'}
        </h1>
        
        <div className="flex items-center gap-4 mb-4 text-muted-foreground">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span>by <span className="font-medium text-foreground">@{creator?.username || 'unknown'}</span></span>
          </div>
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            <span className="font-medium text-foreground">{totalDrops}</span> drops
          </div>
        </div>

        {collection.description && (
          <p className="text-lg text-muted-foreground max-w-3xl">
            {collection.description}
          </p>
        )}
      </div>

      {/* Rarities Grid */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Available Drops</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {rarities.map((rarity) => {
            const color = rarityColors[rarity.rarity];
            return (
              <Card key={rarity.id} className={`overflow-hidden border-2 ${color.border}`}>
                <div className="relative aspect-square bg-black/5">
                  {rarity.media_url ? (
                    <img
                      src={rarity.media_url}
                      alt={`${rarity.rarity} drop`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Sparkles className="w-16 h-16 text-muted-foreground/30" />
                    </div>
                  )}
                  <Badge className={`absolute top-3 left-3 ${color.bg} ${color.text} border-0`}>
                    {rarity.rarity}
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-muted-foreground">Available</div>
                      <div className="text-2xl font-bold">{rarity.count}</div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-xs">
                        {rarity.rarity.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button
          size="lg"
          className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          disabled
        >
          <Sparkles className="w-5 h-5 mr-2" />
          Burn Drop (Coming Soon)
        </Button>
      </div>
    </div>
  );
};

export default DropsCollectionDetail;
